import { ChevronRightIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo } from 'react';
import { Tooltip } from '../Tooltip';

type HeroIcon = React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;

export interface ActionListItemProps {
  icon: HeroIcon;
  title: React.ReactNode;
  description?: React.ReactNode;
  onClick: () => void;
  // For what takes something away: cancelling, removing.
  danger?: boolean;
  // Why it cannot be done right now: the item is then greyed out and says so on hover.
  disabledReason?: React.ReactNode;
}
const ActionListItem_: React.FC<ActionListItemProps> = ({ icon: Icon, title, description, onClick, danger, disabledReason }) => {
  const disabled = !!disabledReason;
  const item = (
    <button type="button" onClick={onClick} disabled={disabled} className={classNames('group flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors', disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-50 dark:hover:bg-white/5')}>
      <span className={classNames('flex h-8 w-8 shrink-0 items-center justify-center rounded-full', danger ? 'bg-error-red-500/10 text-error-red-500' : 'bg-gray-100 dark:bg-white/10')}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="flex min-w-0 grow flex-col">
        <span className={classNames('text-sm font-semibold', danger && 'text-error-red-500')}>{title}</span>
        {description && <span className="truncate text-xs text-gray-500">{description}</span>}
      </span>
      <ChevronRightIcon className={classNames('h-4 w-4 shrink-0 text-gray-400 transition-transform', !disabled && 'group-hover:translate-x-0.5')} />
    </button>
  );
  if (!disabled) return item;
  return (
    <Tooltip message={<span>{disabledReason}</span>} delay={0}>
      {item}
    </Tooltip>
  );
};
export const ActionListItem = memo(ActionListItem_);

// The secondary things to do on a page or in a window, as one list instead of a row of buttons: what each one
// is, in a line, and that it opens something. The one primary action stays a button, next to what it changes.
const ActionList_: React.FC2 = ({ children, className }) => (
  <div className={classNames('flex flex-col divide-y divide-gray-200 overflow-hidden rounded-md border border-gray-300 dark:divide-gray-700 dark:border-gray-700', className)}>{children}</div>
);
export const ActionList = memo(ActionList_);
